import { useCallback, useEffect, useMemo, useState } from "react";
import type { PublicAiCartAction, PublicProduct } from "../../../shared/types";
import { cartLineId } from "../../../shared/cart";
import {
  MAX_CART_LINES,
  configurationIssue,
  configuredUnitPrice,
  useCart,
} from "../../contexts/CartContext";
import type { CartLine } from "../../contexts/CartContext";
import { useTenant } from "../../contexts/TenantContext";
import { useToast } from "../../contexts/ToastContext";
import { usePublicAiStatus } from "../../hooks/businessData";
import {
  createCartConfiguration,
  needsSteps,
} from "./configuration";

const PUBLIC_MENU_BRAND_LOADER_MS = 1800;

function readStoredBranch(): string {
  try {
    return sessionStorage.getItem("menucloud.branch") || "";
  } catch {
    return "";
  }
}

export function usePublicMenuController() {
  const { tenant, loading, error, reload } = useTenant();
  const { add, lines, itemCount, totalUsdCents, setQuantity, remove, clear } =
    useCart();
  const { show } = useToast();
  const [brandLoaderDone, setBrandLoaderDone] = useState(false);
  const [brandLoaderVisible, setBrandLoaderVisible] = useState(true);
  const [activeCategory, setActiveCategory] = useState("");
  const [selectedProduct, setSelectedProduct] = useState<PublicProduct | null>(
    null,
  );
  const [editingLine, setEditingLine] = useState<CartLine | null>(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [branchId, setBranchId] = useState(readStoredBranch);
  const aiStatus = usePublicAiStatus(branchId);

  useEffect(() => {
    const timeoutId = window.setTimeout(
      () => setBrandLoaderDone(true),
      PUBLIC_MENU_BRAND_LOADER_MS,
    );
    return () => window.clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    if (loading || !brandLoaderDone || !brandLoaderVisible) return;
    setBrandLoaderVisible(false);
  }, [brandLoaderDone, brandLoaderVisible, loading]);

  const products = useMemo(
    () =>
      new Map(
        tenant?.categories
          .flatMap((category) => category.products)
          .map((product) => [product.id, product]) ?? [],
      ),
    [tenant],
  );

  useEffect(() => {
    if (!tenant) return;
    const validProducts = new Set(products.keys());
    if (lines.some((line) => !validProducts.has(line.product.id))) clear();
    if (!tenant.categories.some((category) => category.id === activeCategory)) {
      setActiveCategory(tenant.categories[0]?.id ?? "");
    }
    if (!tenant.branches.some((branch) => branch.id === branchId)) {
      const first = tenant.branches[0]?.id ?? "";
      setBranchId(first);
      try {
        if (first) sessionStorage.setItem("menucloud.branch", first);
      } catch {
        // Public menu keeps the branch in memory when sessionStorage is blocked.
      }
    }
  }, [activeCategory, branchId, clear, lines, products, tenant]);

  const selectedBranch = useMemo(
    () => tenant?.branches.find((branch) => branch.id === branchId),
    [branchId, tenant],
  );

  const selectBranch = useCallback((id: string) => {
    setBranchId(id);
    try {
      if (id) sessionStorage.setItem("menucloud.branch", id);
    } catch {
      // Public menu keeps the selection in memory for this visit.
    }
  }, []);

  const openProduct = useCallback((product: PublicProduct) => {
    setEditingLine(null);
    setSelectedProduct(product);
  }, []);

  const editLine = useCallback((line: CartLine) => {
    setCartOpen(false);
    setEditingLine(line);
    setSelectedProduct(line.product);
  }, []);

  const closeCustomizer = useCallback(() => {
    setSelectedProduct(null);
    setEditingLine(null);
  }, []);

  const quickAddProduct = useCallback(
    (product: PublicProduct) => {
      if (!branchId) {
        show("Selecciona una sucursal antes de agregar productos.", "error");
        return false;
      }
      if (needsSteps(product)) {
        openProduct(product);
        return false;
      }
      const configuration = createCartConfiguration(
        product,
        products,
        branchId,
      );
      const issue = configurationIssue(
        product,
        configuration,
        products,
        branchId,
      );
      if (issue) {
        openProduct(product);
        return false;
      }
      const added = add(
        product,
        1,
        configuration,
        configuredUnitPrice(product, configuration, products),
      );
      if (!added) {
        show(
          `El pedido admite como máximo ${MAX_CART_LINES} combinaciones de productos.`,
          "error",
        );
        return false;
      }
      show(`${product.name} agregado al pedido.`, "success");
      return true;
    },
    [add, branchId, openProduct, products, show],
  );

  const applyAiCartAction = useCallback(
    (action: PublicAiCartAction) => {
      if (action.type === "remove") {
        if (!lines.some((line) => line.id === action.lineId)) {
          show("La línea indicada ya no existe en tu carrito.", "error");
          return false;
        }
        remove(action.lineId);
        show("Producto eliminado del pedido.", "success");
        return true;
      }

      const product = products.get(action.line.productId);
      if (!product) {
        show("Ese producto ya no está disponible en el menú actual.", "error");
        return false;
      }
      const issue = branchId
        ? configurationIssue(
            product,
            action.line.configuration,
            products,
            branchId,
          )
        : "Selecciona una sucursal antes de actualizar el pedido.";
      if (issue) {
        show(issue, "error");
        return false;
      }

      if (action.type === "add") {
        const existingLineId = cartLineId(action.line.configuration);
        if (lines.some((line) => line.id === existingLineId)) {
          setQuantity(
            existingLineId,
            action.line.quantity,
            action.line.unitPriceUsdCents,
          );
          show(`${product.name} actualizado en el pedido.`, "success");
          return true;
        }
        const added = add(
          product,
          action.line.quantity,
          action.line.configuration,
          action.line.unitPriceUsdCents,
        );
        if (!added) {
          show(
            `El pedido admite como máximo ${MAX_CART_LINES} combinaciones de productos.`,
            "error",
          );
          return false;
        }
        show(`${product.name} agregado al pedido.`, "success");
        return true;
      }

      if (!lines.some((line) => line.id === action.line.lineId)) {
        show("La línea indicada ya no existe en tu carrito.", "error");
        return false;
      }
      setQuantity(
        action.line.lineId,
        action.line.quantity,
        action.line.unitPriceUsdCents,
      );
      show(`${product.name} actualizado en el pedido.`, "success");
      return true;
    },
    [add, branchId, lines, products, remove, setQuantity, show],
  );

  return {
    activeCategory,
    aiStatus,
    applyAiCartAction,
    branchId,
    brandLoaderVisible,
    cartOpen,
    closeCustomizer,
    editLine,
    editingLine,
    error,
    itemCount,
    lines,
    loading,
    openProduct,
    products,
    quickAddProduct,
    reload,
    selectBranch,
    selectedBranch,
    selectedProduct,
    setActiveCategory,
    setCartOpen,
    tenant,
    totalUsdCents,
  };
}

export type PublicMenuController = ReturnType<typeof usePublicMenuController>;
